import { api } from '../api.js';
import { renderHeader, bindHeader } from '../components/header.js';
import { toast } from '../utils/toast.js';

const CATEGORY_OPTIONS = [
  { value: 'email_not_arrived', label: 'Письмо не пришло' },
  { value: 'wrong_email', label: 'Указал неправильный email' },
  { value: 'login_issue', label: 'Проблема со входом' },
  { value: 'bug', label: 'Нашёл баг' },
  { value: 'feature', label: 'Идея / предложение' },
  { value: 'other', label: 'Другое' },
];

const STATUS_META = {
  open:         { label: 'В работе',   cls: 'status-open' },
  waiting_user: { label: 'Ждём тебя',  cls: 'status-waiting' },
  resolved:     { label: 'Закрыт',     cls: 'status-resolved' },
};

function categoryLabel(code) {
  return CATEGORY_OPTIONS.find((c) => c.value === code)?.label ?? 'Другое';
}

function fmtTime(d) {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleString('ru', { dateStyle: 'short', timeStyle: 'short' });
}
function escapeHtml(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export async function renderSupportPage({ root }) {
  root.innerHTML = renderHeader({ active: 'support' }) + `
    <div class="container support-container">
      <h1 class="admin-title">Поддержка</h1>
      <p class="text-muted" style="max-width: 640px;">Напиши нам в тикет — отвечаем в среднем за пару часов в рабочее время. Один тикет — одна история. Ответы прилетят сюда же и обновят статус.</p>

      <div class="support-layout">
        <div class="support-sidebar">
          <button id="new-ticket-btn" class="btn" style="width:100%;">+ Новое обращение</button>
          <ul id="tickets-list" class="support-tickets"></ul>
        </div>
        <div id="support-main" class="support-main"></div>
      </div>
    </div>
  `;
  bindHeader();

  const newBtn = root.querySelector('#new-ticket-btn');
  const listEl = root.querySelector('#tickets-list');
  const mainEl = root.querySelector('#support-main');

  let tickets = [];
  let selectedId = null;

  async function loadTickets() {
    try {
      const data = await api.support.listMine();
      tickets = data.tickets;
      renderList();
      if (selectedId && !tickets.find((t) => t.id === selectedId)) {
        selectedId = null;
        renderNewForm();
      } else if (!selectedId && !tickets.length) {
        renderNewForm();
      } else if (!selectedId) {
        await openTicket(tickets[0].id);
      }
    } catch (err) {
      listEl.innerHTML = '<li class="text-muted">Не удалось загрузить тикеты</li>';
    }
  }

  function renderList() {
    if (!tickets.length) {
      listEl.innerHTML = '<li class="text-muted support-empty">Пока без обращений</li>';
      return;
    }
    listEl.innerHTML = tickets.map((t) => {
      const meta = STATUS_META[t.status] ?? STATUS_META.open;
      return `
        <li class="support-ticket-row ${t.id === selectedId ? 'active' : ''} ${t.unread ? 'unread' : ''}" data-id="${t.id}">
          <div class="row-top">
            <span class="category">${escapeHtml(categoryLabel(t.category))}</span>
            <span class="status-pill ${meta.cls}">${meta.label}</span>
          </div>
          <div class="row-preview">${escapeHtml(t.lastMessage?.body ?? '')}</div>
          <div class="row-time text-muted">${fmtTime(t.updatedAt)}</div>
        </li>
      `;
    }).join('');
    listEl.querySelectorAll('.support-ticket-row').forEach((li) => {
      li.addEventListener('click', () => openTicket(li.dataset.id));
    });
  }

  newBtn.addEventListener('click', () => {
    selectedId = null;
    renderList();
    renderNewForm();
  });

  function renderNewForm() {
    mainEl.innerHTML = `
      <form id="new-form" class="support-new-form">
        <h2>Новое обращение</h2>
        <div class="field">
          <label>Категория</label>
          <select id="f-category" required>
            ${CATEGORY_OPTIONS.map((c) => `<option value="${c.value}">${c.label}</option>`).join('')}
          </select>
        </div>
        <div class="field">
          <label>Что случилось</label>
          <textarea id="f-message" rows="8" required minlength="5" placeholder="Опиши подробно: что пробовал, что ожидал, что получил"></textarea>
        </div>
        <div class="form-error" id="f-error"></div>
        <div class="actions">
          <button type="submit">Отправить</button>
        </div>
      </form>
    `;
    const form = mainEl.querySelector('#new-form');
    const err = mainEl.querySelector('#f-error');
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      err.textContent = '';
      const category = form.querySelector('#f-category').value;
      const message = form.querySelector('#f-message').value.trim();
      if (message.length < 5) { err.textContent = 'Слишком коротко'; return; }
      try {
        const { ticket } = await api.support.create({ category, message });
        toast('Отправили — ждём ответа', { type: 'success' });
        selectedId = ticket.id;
        await loadTickets();
      } catch (e2) {
        err.textContent = e2.message || 'Не удалось отправить';
      }
    });
  }

  async function openTicket(id) {
    selectedId = id;
    renderList();
    mainEl.innerHTML = '<div class="text-muted">Загрузка…</div>';
    try {
      const { ticket } = await api.support.show(id);
      renderThread(ticket);
      // refresh list to drop unread
      tickets = tickets.map((t) => (t.id === id ? { ...t, unread: false } : t));
      renderList();
    } catch {
      mainEl.innerHTML = '<div class="text-muted">Не удалось открыть тикет</div>';
    }
  }

  function renderThread(ticket) {
    const meta = STATUS_META[ticket.status] ?? STATUS_META.open;
    const msgs = ticket.messages.map((m) => `
      <div class="thread-msg thread-msg-${m.sender}">
        <div class="thread-msg-meta text-muted">${m.sender === 'admin' ? 'Chunkly support' : 'Ты'} · ${fmtTime(m.createdAt)}</div>
        <div class="thread-msg-body">${escapeHtml(m.body).replace(/\n/g, '<br>')}</div>
      </div>
    `).join('');

    const canReply = ticket.status !== 'resolved';
    mainEl.innerHTML = `
      <div class="thread-head">
        <div>
          <div class="thread-category">${escapeHtml(categoryLabel(ticket.category))}</div>
          ${ticket.subject ? `<h2>${escapeHtml(ticket.subject)}</h2>` : ''}
          <div class="text-muted" style="font-size: 0.82rem; margin-top: 4px;">Создан ${fmtTime(ticket.createdAt)}</div>
        </div>
        <span class="status-pill ${meta.cls}">${meta.label}</span>
      </div>
      <div class="thread-body">${msgs}</div>
      ${canReply ? `
        <form id="reply-form" class="thread-reply">
          <textarea id="reply-input" rows="4" placeholder="Напиши ответ…" required minlength="1"></textarea>
          <div class="form-error" id="reply-error"></div>
          <div class="actions"><button type="submit">Отправить</button></div>
        </form>
      ` : `<div class="text-muted" style="padding: 14px 0;">Тикет закрыт. Если вопрос вернулся — создай новое обращение.</div>`}
    `;
    const form = mainEl.querySelector('#reply-form');
    if (form) {
      const err = mainEl.querySelector('#reply-error');
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        err.textContent = '';
        const message = form.querySelector('#reply-input').value.trim();
        if (!message) return;
        try {
          await api.support.addMessage(ticket.id, message);
          await openTicket(ticket.id);
          await loadTickets();
        } catch (e2) {
          err.textContent = e2.message || 'Не удалось отправить';
        }
      });
    }
  }

  await loadTickets();
}
